import { forwardRef, useRef, ReactNode } from "react";
import {
  Accordion as ChakraAccordion,
  AccordionRootProps as ChakraAccordionRootProps,
} from "@chakra-ui/react";
import { AccordionIndexContext } from "./AccordionContext";
import AccordionItem from "./AccordionItem.web";
import AccordionTrigger from "./AccordionTrigger.web";
import AccordionContent from "./AccordionContent.web";

type AccordionStyleProps = {
  border?: string;
  borderColor?: string;
  marginTop?: string | number;
  mt?: string | number;
  width?: string;
  display?: string | Record<string, string>;
};

export type AccordionProps = {
  children?: ReactNode;
  /**
   * Allow multiple items to be expanded simultaneously
   */
  multiple?: boolean;
  /**
   * Allow items to be collapsed (closed) after being opened
   */
  collapsible?: boolean;
  /**
   * Default expanded item(s) by index
   */
  defaultIndex?: number | number[];
  /**
   * Default expanded item(s) by value
   */
  defaultValue?: string[];
  /**
   * Controlled expanded item(s) by index
   */
  index?: number | number[];
  /**
   * Controlled expanded item(s) by value
   */
  value?: string[];
  /**
   * Callback when expanded items change
   */
  onChange?: (value: string[]) => void;
  /**
   * Make collapse content overflow visible (fixes overflow hidden on .chakra-collapse)
   */
  collapseOverflowVisible?: boolean;
} & AccordionStyleProps &
  Omit<
    ChakraAccordionRootProps,
    "multiple" | "collapsible" | "defaultValue" | "value"
  >;

const Accordion = forwardRef<HTMLDivElement, AccordionProps>((props, ref) => {
  const {
    children,
    multiple = false,
    collapsible = false,
    defaultIndex,
    defaultValue,
    index,
    value,
    onChange,
    border,
    borderColor,
    marginTop,
    mt,
    width,
    display,
    collapseOverflowVisible,
    ...rest
  } = props;

  // Map defaultIndex to defaultValue
  let resolvedDefaultValue = defaultValue;
  if (resolvedDefaultValue === undefined && defaultIndex !== undefined) {
    if (Array.isArray(defaultIndex)) {
      resolvedDefaultValue = defaultIndex.map((i) => String(i));
    } else {
      resolvedDefaultValue = [String(defaultIndex)];
    }
  }

  // Map index to value for controlled mode
  let resolvedValue = value;
  if (resolvedValue === undefined && index !== undefined) {
    if (Array.isArray(index)) {
      resolvedValue = index.map((i) => String(i));
    } else if (index === -1) {
      resolvedValue = [];
    } else {
      resolvedValue = [String(index)];
    }
  }

  // Provider for auto-generating sequential item values
  const counterRef = useRef(0);
  counterRef.current = 0;

  const contextValue = {
    getNextIndex: () => {
      const idx = counterRef.current;
      counterRef.current += 1;
      return idx;
    },
  };

  const styleProps = {
    ...(border !== undefined ? { border } : { border: "none" }),
    ...(borderColor !== undefined ? { borderColor } : {}),
    ...(marginTop !== undefined || mt !== undefined
      ? { mt: marginTop ?? mt }
      : {}),
    ...(width !== undefined ? { width } : {}),
    ...(display !== undefined ? { display } : {}),
  };

  const cssProps = collapseOverflowVisible
    ? {
        css: {
          ".chakra-collapse": { overflow: "visible !important" },
        },
      }
    : {};

  return (
    <AccordionIndexContext.Provider value={contextValue}>
      <ChakraAccordion.Root
        ref={ref}
        multiple={multiple}
        collapsible={collapsible}
        defaultValue={resolvedDefaultValue}
        value={resolvedValue}
        onValueChange={
          onChange
            ? (details: { value: string[] }) => onChange(details.value)
            : undefined
        }
        {...styleProps}
        {...cssProps}
        {...rest}
      >
        {children}
      </ChakraAccordion.Root>
    </AccordionIndexContext.Provider>
  );
});

Accordion.displayName = "Accordion";

// Compound component type
type AccordionComponent = typeof Accordion & {
  Item: typeof AccordionItem;
  Trigger: typeof AccordionTrigger;
  Content: typeof AccordionContent;
};

const AccordionWithCompounds = Accordion as AccordionComponent;
AccordionWithCompounds.Item = AccordionItem;
AccordionWithCompounds.Trigger = AccordionTrigger;
AccordionWithCompounds.Content = AccordionContent;

export default AccordionWithCompounds;
